import { ArrowBackIosNew } from '@mui/icons-material';
import { Box, Menu, MenuItem, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

export const SelectDropdown = ({
  options,
  selectedOption,
  onSave,
  placeholder = 'Select an option...',
}: {
  options: Record<string, { text: string }>;
  selectedOption: string;
  onSave: (option: string) => void;
  placeholder?: string;
}) => {
  const anchorEl = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = useState(360);

  const handleSelect = (option: string) => {
    onSave(option);
  };

  useEffect(() => {
    if (!isOpen || !anchorEl.current) {
      return;
    }

    const updateMenuHeight = () => {
      if (!anchorEl.current) {
        return;
      }

      const viewportPadding = 24;
      const anchorRect = anchorEl.current.getBoundingClientRect();
      const availableHeight =
        window.innerHeight - anchorRect.bottom - viewportPadding;

      setMenuMaxHeight(
        Math.min(360, Math.max(120, Math.floor(availableHeight))),
      );
    };

    updateMenuHeight();
    window.addEventListener('resize', updateMenuHeight);

    return () => {
      window.removeEventListener('resize', updateMenuHeight);
    };
  }, [isOpen]);

  return (
    <>
      <Box
        ref={anchorEl}
        sx={{
          display: 'flex',
          height: 45,
          padding: 1.5,
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: 2,
          border: theme =>
            `${isOpen ? 2 : 1}px solid ${isOpen ? theme.palette.secondary.main : theme.palette.grey100}`,
          bgcolor: 'white',
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'secondary.main',
          },
        }}
        onClick={() => setIsOpen(prev => !prev)}>
        <Typography
          variant={selectedOption ? 'body2SemiBold' : 'body2'}
          sx={{
            color: selectedOption ? 'primary.main' : 'grey100',
          }}>
          {options[selectedOption]?.text || placeholder}
        </Typography>
        <ArrowBackIosNew
          sx={{
            transform: isOpen ? 'rotate(90deg)' : 'rotate(-90deg)',
            color: 'text.main',
            width: 16,
            height: 16,
            transition: 'transform 0.2s ease-in-out',
          }}
        />
      </Box>
      <Menu
        anchorEl={anchorEl.current}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        disableAutoFocusItem
        marginThreshold={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: anchorEl.current?.clientWidth,
              maxHeight: `${menuMaxHeight}px`,
              minHeight: 0,
              overflowY: 'auto',
            },
          },
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
          },
          '& .MuiMenu-list': {
            p: 1,
            maxHeight: 'inherit',
            overflowY: 'auto',
            scrollbarWidth: 'thin',
          },
        }}>
        {Object.keys(options).map(key => (
          <MenuItem
            key={key}
            value={key}
            onClick={() => {
              handleSelect(key);
              setIsOpen(false);
            }}
            sx={{
              borderRadius: 1,
              color: 'primary.main',
              padding: 1.5,
              '&.Mui-selected': {
                bgcolor: 'secondary.light',
                color: 'secondary.main',
              },
            }}
            selected={selectedOption === key}>
            <Typography
              variant={selectedOption === key ? 'body2SemiBold' : 'body2'}
              sx={{ color: 'inherit' }}>
              {options[key].text}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
